/**
 * Entity navigation tools. Expose the agent's parent, parent chain, children,
 * siblings, and arbitrary-id lookups — gated by `canReadEntity(ctx.userId, ...)`
 * so the agent only sees what the invoking user can read (owned, READ-ACL,
 * WRITE-ACL, or PUBLIC/AUTHENTICATED).
 *
 * The ACL invariant is structural: every entity-data egress passes through
 * either `readEntityForUser` (single-entity) or `filterReadable` (lists). No
 * tool calls `deps.loadEntity` or `deps.loadChildren` and returns the raw
 * result. A code reviewer can grep for these two helpers and know every
 * return path is gated.
 *
 * See `docs/superpowers/specs/2026-05-15-agent-tools-api-design.md` §4a +
 * §12 for the design rationale and the 10 mandatory ACL tests.
 */
import type { EntitySnapshot, StructureOutline } from '@tunasong/agent-context'
import { canReadEntity } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'
import { z } from 'zod'
import type { AnyTool, Tool, ToolCallContext } from '../types.js'

// ───────────────────────────────────────────────────────────────────────────────
// Deps + chokepoints
// ───────────────────────────────────────────────────────────────────────────────

export interface EntityNavigationDeps {
  /**
   * Load the raw persisted entity (`userId` + `acls` intact) so the handler
   * can ACL-gate before exposing anything. Returns `null` when the entity is
   * not found — distinguishable from "not readable" only in the deps layer;
   * the tool surface treats both as the same outcome (existence-leak prevention).
   */
  loadEntity: (id: string) => Promise<Persisted<Entity> | null>
  /**
   * Load raw children of a parent. The DynamoDB parent-index intentionally
   * does NOT filter by userId (it supports public-shared sub-entities), so
   * the handler must filter the raw result through `canReadEntity`.
   */
  loadChildren: (parentId: string, type?: string) => Promise<Persisted<Entity>[]>
  /**
   * Build the full LLM-facing snapshot (with body text + outline) from a raw
   * entity. Shared with `load-context` so the projection is single-sourced.
   */
  toSnapshot: (entity: Persisted<Entity>) => Promise<{ snapshot: EntitySnapshot; outline?: StructureOutline }>
}

/** The single sentinel error returned for both "not found" and "no read access". */
const ENTITY_DENIED = 'entity not found or no read access' as const

interface ReadOk {
  ok: true
  entity: Persisted<Entity>
}
interface ReadDenied {
  ok: false
  error: typeof ENTITY_DENIED
}

/**
 * Single chokepoint for entity-data egress. Returns the entity only when
 * `canReadEntity(ctx.userId, entity)` admits it. The error string is the
 * same whether the entity is missing or unreadable — don't differentiate
 * to the LLM (existence-leak prevention; §12.3 test #7).
 */
const readEntityForUser = async (
  deps: EntityNavigationDeps,
  ctx: ToolCallContext,
  id: string
): Promise<ReadOk | ReadDenied> => {
  const entity = await deps.loadEntity(id)
  if (!entity || !canReadEntity(ctx.userId, entity)) {
    return { ok: false, error: ENTITY_DENIED }
  }
  return { ok: true, entity }
}

/**
 * List chokepoint. Drops entities the invoking user cannot read. Apply
 * after `deps.loadChildren` because the parent index does not pre-filter.
 */
const filterReadable = (entities: Persisted<Entity>[], ctx: ToolCallContext): Persisted<Entity>[] =>
  entities.filter(e => canReadEntity(ctx.userId, e))

// ───────────────────────────────────────────────────────────────────────────────
// Lightweight projection (no body/outline; cheap for list responses)
// ───────────────────────────────────────────────────────────────────────────────

/** Project a raw entity into a lightweight `EntitySnapshot` — id/type/name/tags/dates only. */
const toLightweightSnapshot = (entity: Persisted<Entity>): EntitySnapshot => ({
  id: entity.id,
  type: entity.type,
  name: entity.name,
  description: entity.description,
  tags: entity.tags,
  createdAt: entity.createdAt,
  updatedAt: entity.updatedAt,
})

// ───────────────────────────────────────────────────────────────────────────────
// Schemas
// ───────────────────────────────────────────────────────────────────────────────

const entityIdArg = z.string().min(1).describe('The id of an entity to load.')

const entityTypeArg = z
  .string()
  .min(1)
  .describe('Optional entity-type filter, e.g. "song" or "audio".')
  .optional()

const emptySchema = z.object({})
const listSchema = z.object({ type: entityTypeArg })
const getEntitySchema = z.object({ entityId: entityIdArg })

// ───────────────────────────────────────────────────────────────────────────────
// Tools
// ───────────────────────────────────────────────────────────────────────────────

/**
 * 1. `get_parent_entity` — return the agent's parent entity in full
 *    (snapshot + structural outline), gated by `canReadEntity`.
 */
export const createGetParentEntityTool = (
  deps: EntityNavigationDeps
): Tool<z.infer<typeof emptySchema>, { snapshot: EntitySnapshot; outline?: StructureOutline }> => ({
  name: 'get_parent_entity',
  description:
    'Return the entity this agent is attached to (its parent). Includes the full snapshot (id, type, name, body text) and the structural outline (sections, chords, child counts). Use this to ground reasoning in the actual content of the song/page the agent lives on.',
  schema: emptySchema,
  handler: async (_args, ctx) => {
    const gate = await readEntityForUser(deps, ctx, ctx.parentEntityId)
    if (!gate.ok) {
      return { ok: false, error: gate.error }
    }
    const projected = await deps.toSnapshot(gate.entity)
    return { ok: true, result: projected }
  },
})

/**
 * 2. `list_child_entities` — children of the agent's parent, filtered to
 *    those the invoking user can read. Returns lightweight snapshots (no
 *    body text or outline) to keep token cost predictable when listings
 *    are large.
 */
export const createListChildEntitiesTool = (
  deps: EntityNavigationDeps
): Tool<z.infer<typeof listSchema>, EntitySnapshot[]> => ({
  name: 'list_child_entities',
  description:
    'List the children of the agent\'s parent entity. Returns lightweight snapshots (id, type, name, tags, dates) without body text — call `get_entity` to drill into a specific child. Optional `type` filter narrows by entity type (e.g. "audio", "audioclip", "todo", "comment").',
  schema: listSchema,
  handler: async (args, ctx) => {
    const raw = await deps.loadChildren(ctx.parentEntityId, args.type)
    const readable = filterReadable(raw, ctx)
    return { ok: true, result: readable.map(toLightweightSnapshot) }
  },
})

/**
 * 3. `list_sibling_entities` — children of the agent's parent's parent,
 *    excluding the agent's parent itself. Gates the grandparent through
 *    `canReadEntity` (so if the user lost read access to the surrounding
 *    folder, the agent can't enumerate it) and filters the resulting
 *    children. Returns `[]` when there's no grandparent (parent is root).
 */
export const createListSiblingEntitiesTool = (
  deps: EntityNavigationDeps
): Tool<z.infer<typeof listSchema>, EntitySnapshot[]> => ({
  name: 'list_sibling_entities',
  description:
    'List sibling entities of the agent\'s parent — other children of the parent\'s parent, excluding the parent itself. Returns lightweight snapshots. Empty when the parent has no parent (e.g., the parent is a top-level folder). Optional `type` filter.',
  schema: listSchema,
  handler: async (args, ctx) => {
    const parentRead = await readEntityForUser(deps, ctx, ctx.parentEntityId)
    if (!parentRead.ok) {
      return { ok: false, error: parentRead.error }
    }
    const grandparentId = parentRead.entity.parentId
    if (!grandparentId) {
      // Parent is a root entity — no siblings to list.
      return { ok: true, result: [] }
    }
    const grandparentRead = await readEntityForUser(deps, ctx, grandparentId)
    if (!grandparentRead.ok) {
      // Cannot read the grandparent → cannot enumerate its children. Return
      // the same sentinel error; the agent should not learn the grandparent
      // exists if the user cannot read it.
      return { ok: false, error: grandparentRead.error }
    }
    const raw = await deps.loadChildren(grandparentId, args.type)
    const readable = filterReadable(raw, ctx).filter(e => e.id !== ctx.parentEntityId)
    return { ok: true, result: readable.map(toLightweightSnapshot) }
  },
})

/**
 * 4. `get_entity` — the hot path for arbitrary-id lookup. Returns the full
 *    snapshot + outline when the user can read it; otherwise returns
 *    `{ ok: false, error: 'entity not found or no read access' }` — same
 *    string whether the entity exists. Don't leak existence to the LLM.
 */
export const createGetEntityTool = (
  deps: EntityNavigationDeps
): Tool<z.infer<typeof getEntitySchema>, { snapshot: EntitySnapshot; outline?: StructureOutline }> => ({
  name: 'get_entity',
  description:
    'Fetch a specific entity by id. Returns the full snapshot (id, type, name, body text) and structural outline. Returns an error if the entity does not exist or the invoking user lacks read access — the error message does not distinguish between these cases.',
  schema: getEntitySchema,
  handler: async (args, ctx) => {
    const gate = await readEntityForUser(deps, ctx, args.entityId)
    if (!gate.ok) {
      return { ok: false, error: gate.error }
    }
    const projected = await deps.toSnapshot(gate.entity)
    return { ok: true, result: projected }
  },
})

/**
 * 5. `get_parent_chain` — the path from the agent's parent up to the root,
 *    one link per ancestor. ACL-gated *per step*: the chain is truncated at
 *    the first link the user cannot read (rather than returning the
 *    readable suffix beyond a hidden parent — that would leak structural
 *    information). Caps at MAX_CHAIN_DEPTH to prevent runaway walks on a
 *    cyclic parentId graph.
 */
const MAX_CHAIN_DEPTH = 50

interface ChainLink {
  id: string
  type: string
  name?: string
}

export const createGetParentChainTool = (
  deps: EntityNavigationDeps
): Tool<z.infer<typeof emptySchema>, ChainLink[]> => ({
  name: 'get_parent_chain',
  description:
    'Return the chain of ancestors from the agent\'s parent up to the root, one entry per ancestor (id, type, name). The agent\'s direct parent is index 0; the root is at the end. Useful for understanding where the agent lives in the user\'s library hierarchy. Truncated at the first ancestor the user cannot read.',
  schema: emptySchema,
  handler: async (_args, ctx) => {
    const chain: ChainLink[] = []
    const seen = new Set<string>()
    let currentId: string | undefined = ctx.parentEntityId
    let depth = 0
    while (currentId && depth < MAX_CHAIN_DEPTH) {
      if (seen.has(currentId)) {
        // Cycle in the parentId graph — refuse to walk further. This is
        // defensive; the DB should not produce cycles, but if it does we
        // return what we have rather than looping forever.
        break
      }
      seen.add(currentId)
      const gate = await readEntityForUser(deps, ctx, currentId)
      if (!gate.ok) {
        // The IMMEDIATE parent is unreadable → return the error (caller
        // can't usefully consume a chain that starts with a black box).
        // For DEEPER ancestors that become unreadable, we just stop
        // walking — the readable prefix is fine.
        if (chain.length === 0) {
          return { ok: false, error: gate.error }
        }
        break
      }
      chain.push({ id: gate.entity.id, type: gate.entity.type, name: gate.entity.name })
      currentId = gate.entity.parentId
      depth += 1
    }
    return { ok: true, result: chain }
  },
})

// ───────────────────────────────────────────────────────────────────────────────
// Catalog
// ───────────────────────────────────────────────────────────────────────────────

/**
 * All 5 entity navigation tools wired against the same deps. Exposed as an
 * array for one-call registration via `createRegistry`.
 */
export const createEntityNavigationTools = (deps: EntityNavigationDeps): AnyTool[] => [
  createGetParentEntityTool(deps),
  createListChildEntitiesTool(deps),
  createListSiblingEntitiesTool(deps),
  createGetEntityTool(deps),
  createGetParentChainTool(deps),
]
